/* eslint-disable complexity */

import {
  Box,
  CircularProgress,
  CollapsibleCard,
  FormLabel,
  Grid,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { CheckCircleOutlineIcon, CreditCardIcon } from '@atgfr8/icons';
import { statusOptions } from '@common/commonLists';
import { hasQualifiedStatus } from '@common/order/helperFunctions';
import { CautionInput, Notification, RadioGroup } from '@components';
import { SelectInput } from '@components/Select/SelectInput';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  TrackingProviders,
  UserInfoModel,
  customerStore,
  fetchOrder,
  fetchUserInfoUserModel,
  hasTrackingId,
  isCreditLimitExceeded,
  isOrderInNegativeCreditWarningZone,
  load,
  markPrePaymentProcessed,
  orderContainsLTLLoad,
  orderStore,
  setOrderStore,
  updateOrderState,
} from '@store/orders';
import { userStore } from '@store/user';
import { CustomerPrePaymentViewModel } from '@typeDefinitions/customerTypes';
import { FlagViewModel } from '@typeDefinitions/flagTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { formatAmount, isAdmin } from '@utils/utils';
import { CustomerInfoHeader } from '@views/order/components/customer/CustomerInfoHeader';
import { For, Show, createEffect, createSignal } from 'solid-js';

import {
  CreditAcknowledgementModal,
  CreditIncreaseRequestModal,
} from '../models';
import classes from './classes';
import { CreditLimitBanner } from './CreditLimitBanner';
import CustomerContactInformationDetails from './CustomerContactInformationDetails';
import CustomerEDI from './CustomerEDI';
import { BillingHoldFlagV2 } from './CustomerInformation/components';
import CustomerInformationTabs from './CustomerInformationTabs';
import { CustomerInvoice } from './CustomerInvoice';
import CustomerSelection from './CustomerSelection';
import CustomerWarnings from './CustomerWarnings';
import { ReferenceWarning } from './ReferenceWarning';

export const CustomerInformation = () => {
  const adjustmentsErrorKeys = [
    'revisitRetractionDate',
    'badAdjustments',
    'exceptionReason',
  ];
  const [customerWarnings, setCustomerWarnings] = createSignal<string[] | null>(
    null,
  );
  const [isRefDirty, setIsRefDirty] = createSignal(false);
  const [orderRepInformation, setOrderRepInformation] =
    createSignal<UserInfoModel>();

  const cls = classes.customerInfo;

  createEffect(async () => {
    if (orderStore.isCreate && userStore.user.id) {
      const response = await fetchUserInfoUserModel(userStore.user.id);
      setOrderRepInformation(response);
      updateOrderState({
        owner: response.name,
        assignedId: response.id,
        orderRep: response,
      });
    }
  });
  createEffect(() => {
    if (orderStore.resetDirty) {
      setIsRefDirty(false);
    }
  });

  const handleMetadataChange = (value: string, idx: number) => {
    let tempMetadata = [...(orderStore.order.metadata ?? [])][idx];
    tempMetadata = { ...tempMetadata, value: value, operationType: 'Update' };
    setOrderStore('order', 'metadata', idx, tempMetadata);
  };

  const hasPrepaymentsToDisplay = () =>
    orderStore.order.customerPrePayments != null &&
    orderStore.order.customerPrePayments.filter((cpp) => cpp.processed == false)
      .length > 0;

  const prepaymentsTotal = () =>
    orderStore.order.customerPrePayments == null
      ? 0
      : orderStore.order.customerPrePayments
          .filter((cpp) => cpp.processed == false)
          .reduce((total, cpp) => {
            return cpp.paidAmount + total;
          }, 0);

  const totalPrepayments = () =>
    orderStore.order.customerPrePayments == null
      ? 0
      : orderStore.order.customerPrePayments.filter(
          (cpp) => cpp.processed == false,
        ).length;

  const [processingPrePaymentIds, setProcessingPrePaymentIds] = createSignal<
    Set<string>
  >(new Set());

  const paymentLineItemsTotal = () =>
    orderStore.order.payments.length > 0
      ? orderStore.order.payments
          .filter((p) => p.operationType !== 'Delete')
          .reduce((acc, item) => acc + Number(item.amount), 0)
      : 0;

  const canMarkPrePaymentProcessed = (
    prePayment: CustomerPrePaymentViewModel,
  ) => paymentLineItemsTotal() >= prePayment.paidAmount;

  const unprocessedPrePayments = () =>
    (orderStore.order.customerPrePayments ?? []).filter(
      (cpp) => cpp.processed === false,
    );

  const handleMarkPrePaymentProcessed = async (prePaymentId: string) => {
    setProcessingPrePaymentIds((prev) => new Set([...prev, prePaymentId]));
    try {
      const success = await markPrePaymentProcessed(
        prePaymentId,
        orderStore.order.id,
      );
      if (Boolean(success)) {
        const orderData = await fetchOrder(String(orderStore.order.id));
        setOrderStore('order', orderData);
      }
    } finally {
      setProcessingPrePaymentIds((prev) => {
        const next = new Set(prev);
        next.delete(prePaymentId);
        return next;
      });
    }
  };

  const isCustomerPresent = () => orderStore.order.customerId !== null;

  const shouldShowCreditLimitBanner = (): boolean =>
    Boolean(
      isFeatureFlagEnabled(
        FeatureFlags.feature_order_block_save_on_neg_credit_limit,
      )() &&
      (isCreditLimitExceeded() || isOrderInNegativeCreditWarningZone()) &&
      !orderContainsLTLLoad() &&
      (orderStore.isCreate ||
        orderStore.order.loads.some((l) => {
          return hasQualifiedStatus(l.status);
        })),
    );

  return (
    <CollapsibleCard
      startTitle="Customer Information"
      class="mb-2"
      contentClass={
        customerWarnings() ||
        (isFeatureFlagEnabled(
          FeatureFlags.feature_order_block_save_on_neg_credit_limit,
        )() &&
          isCreditLimitExceeded() &&
          !orderContainsLTLLoad() &&
          (orderStore.isCreate ||
            orderStore.order.loads.some((l) => {
              return hasQualifiedStatus(l.status);
            })))
          ? 'bg-error-red!'
          : undefined
      }
      loading={orderStore.loading || customerStore.loading}
    >
      <Show
        when={
          orderStore.orderFormError &&
          adjustmentsErrorKeys.some((key) => key in orderStore.orderFormError!)
        }
      >
        <Box gap={1} class="flex flex-col" mb={1}>
          {adjustmentsErrorKeys.map(
            (key) =>
              orderStore.orderFormError &&
              orderStore.orderFormError[key] && (
                <Notification
                  type="error"
                  text={orderStore.orderFormError[key]}
                  tableRowNotification={true}
                />
              ),
          )}
        </Box>
      </Show>
      <Show when={!orderStore.isCreate && !orderStore.order.hideCustomerName}>
        <CustomerInfoHeader />
      </Show>
      <CustomerSelection setCustomerWarnings={setCustomerWarnings} />
      <CustomerWarnings
        customerWarnings={customerWarnings}
        setCustomerWarnings={setCustomerWarnings}
      />
      <Show when={shouldShowCreditLimitBanner()}>
        <div class="mt-2">
          <CreditLimitBanner />
        </div>
      </Show>
      <Show when={orderStore.orderFormError?.referenceNumber}>
        <div class="mb-2">
          <Notification
            type="tableError"
            tableRowNotification
            text={<Box>Reference Number Must Be Less Than 120 Characters.</Box>}
          />
        </div>
      </Show>
      <Show when={isCustomerPresent()}>
        <CustomerInvoice />
      </Show>
      <Show when={!orderStore.isMobile || !orderStore.isCreate}>
        <CustomerContactInformationDetails
          orderRepInformation={orderRepInformation}
          setOrderRepInformation={setOrderRepInformation}
        />
        <CustomerEDI />
        <div class="flex gap-4 flex-wrap mt-4">
          <Show
            when={
              isRefDirty() &&
              load()?.trackingServiceId === TrackingProviders.TruckerTools &&
              hasTrackingId()
            }
          >
            <ReferenceWarning />
          </Show>
          <div class="w-full">
            <CautionInput
              resetDirty={orderStore.resetDirty}
              label="Reference"
              value={orderStore.order.referenceNumber ?? ''}
              caution={Boolean(
                load()?.trackingServiceId === TrackingProviders.TruckerTools &&
                hasTrackingId(),
              )}
              disabled={
                orderStore.isReadOnly ||
                (!isAdmin() && orderStore.order.loadTenderId != null)
              }
              onInput={(e: { target: { value: string } }) => {
                if (e.target.value === '') {
                  setIsRefDirty(false);
                } else {
                  setIsRefDirty(true);
                }
              }}
              onChange={(e: { target: { value: string } }) => {
                updateOrderState({ referenceNumber: e.target.value });
              }}
            />
          </div>
          <div class="min-w-[33%]">
            {isAdmin() ? (
              <SelectInput
                dataTestId="customer-information-status-select"
                label="Status"
                menuItems={statusOptions}
                placeholder="Select Status"
                value={orderStore.order.status}
                disabled={orderStore.isReadOnly}
                onChange={(value) => {
                  updateOrderState({ status: value });
                }}
              />
            ) : (
              <div class="w-fit flex flex-col gap-2">
                <FormLabel
                  class="mb-[-6px] text-xs"
                  style={{ 'font-size': '14px' }}
                >
                  Status
                </FormLabel>
                <span>{orderStore.order.status}</span>
              </div>
            )}
          </div>
          <div class="w-fit">
            <RadioGroup
              label="Bid Type"
              direction="vertical"
              value={orderStore.order.bidType ?? undefined}
              options={[
                { label: 'Spot Market', value: 'Spot Market' },
                { label: 'Contract', value: 'Contract' },
              ]}
              onChange={(value) => updateOrderState({ bidType: value })}
              labelClass="mb-[-6px] text-xs"
              disabled={orderStore.isReadOnly}
              error={orderStore.orderFormError?.['bidType'] ?? null}
            />
          </div>
        </div>
        <Show
          when={
            orderStore.order.bidType !== 'Contract' &&
            Boolean(orderStore.order.bidType)
          }
        >
          <Box class="flex gap-4 mb-[16px] [&>div]:flex-1">
            <TextField
              label="Spot ID"
              placeholder="Spot Id"
              value={orderStore.order.spotId ?? ''}
              onChange={(value: string) => {
                updateOrderState({ spotId: value });
              }}
              class="flex-1"
              disabled={orderStore.isReadOnly}
              size="sm"
              error={
                orderStore.orderFormError && orderStore.orderFormError['spotId']
              }
              dataTestId={'spot-id-textInput'}
            />
            <TextField
              label="Spot Quote"
              placeholder="Spot Quote"
              type="number"
              value={orderStore.order.spotQuote ?? ''} //setting this to empty string to avoid warnings
              onChange={(value: unknown) => {
                updateOrderState({ spotQuote: value as number });
              }}
              error={
                orderStore.orderFormError &&
                orderStore.orderFormError['spotQuote']
              }
              class="flex-1"
              disabled={orderStore.isReadOnly}
              size="sm"
              dataTestId={'spot-quote-textInput'}
            />
          </Box>
        </Show>
        <Show
          when={isFeatureFlagEnabled(FeatureFlags.ShowCustomerInstructions)()}
        >
          <Box fontSize="14px" fontWeight={600} color="#123b50" pb="10px">
            Rate Confirmation Special Instructions
          </Box>
          <Box
            fontSize="14px"
            maxHeight="100px"
            overflow="auto"
            class="whitespace-pre-line"
          >
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
            {customerStore.customer.specialInstructions ?? '-'}
          </Box>
        </Show>
        <Show
          when={isFeatureFlagEnabled(FeatureFlags.ShowCustomerInstructions)()}
        >
          <Box fontSize="14px" fontWeight={600} color="#123b50" pb="10px">
            BOL Special Instructions
          </Box>
          <Box
            fontSize="14px"
            maxHeight="100px"
            overflow="auto"
            class="whitespace-pre-line"
          >
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
            {customerStore.customer.bolInstructions ?? '-'}
          </Box>
        </Show>
        <Show when={hasPrepaymentsToDisplay()}>
          <div class="rounded mb-4 p-2 bg-[#026EA114]">
            <div class="flex items-center">
              <CreditCardIcon class="text-armstrong-primary" />
              <span class="pl-2 text-sm text-[#000000DE]!">
                {`${totalPrepayments()} payment totaling:`}
              </span>
              <span class="pl-2 text-sm text-armstrong-primary font-semibold">
                {formatAmount(prepaymentsTotal())}
              </span>
            </div>
            <Show when={isAdmin()}>
              <div class="mt-2 flex flex-col gap-1">
                <For each={unprocessedPrePayments()}>
                  {(prePayment) => (
                    <div
                      class="flex mobile:flex-col nonMobile:flex-row items-start nonMobile:items-center justify-between rounded bg-white px-3 py-1.5 gap-2"
                      data-testid={`prepayment-row-${prePayment.id}`}
                    >
                      <div class="flex items-center gap-3 text-sm min-w-0 flex-wrap">
                        <span class="text-[#000000DE]">
                          {formatAmount(prePayment.paidAmount)}
                        </span>
                        <Show when={prePayment.referenceNumber}>
                          <span class="text-[#00000099] break-all">
                            Ref: {prePayment.referenceNumber}
                          </span>
                        </Show>
                      </div>
                      <button
                        type="button"
                        class={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors ${
                          canMarkPrePaymentProcessed(prePayment) &&
                          !processingPrePaymentIds().has(prePayment.id)
                            ? 'bg-[#026EA1] text-white hover:bg-[#015a84] cursor-pointer'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                        disabled={
                          !canMarkPrePaymentProcessed(prePayment) ||
                          processingPrePaymentIds().has(prePayment.id)
                        }
                        onClick={() =>
                          handleMarkPrePaymentProcessed(prePayment.id)
                        }
                        data-testid={`mark-processed-btn-${prePayment.id}`}
                      >
                        <Show
                          when={!processingPrePaymentIds().has(prePayment.id)}
                          fallback={
                            <CircularProgress size={14} color="inherit" />
                          }
                        >
                          <CheckCircleOutlineIcon size={'14px'} />
                        </Show>
                        Mark Processed
                      </button>
                    </div>
                  )}
                </For>
              </div>
            </Show>
          </div>
        </Show>
        <Show when={(orderStore.order.metadata ?? []).length > 0}>
          <div class="mb-4">
            <strong
              class={!isCustomerPresent() ? 'text-[#000000] opacity-40' : ''}
            >
              Shipment Metadata
            </strong>
          </div>
          <Grid container spacing={4}>
            <For each={orderStore.order.metadata}>
              {(item, idx) => (
                <Grid item size={6}>
                  <TextField
                    label={item.propertyName}
                    placeholder={item.propertyName}
                    value={item.value}
                    disabled={item.isReadOnly}
                    onChange={(value: string) => {
                      handleMetadataChange(value, idx());
                    }}
                    size="sm"
                    dataTestId={`${(item.propertyName ?? '').replace(/\s+/g, '-').toLowerCase()}-textInput`}
                  />
                </Grid>
              )}
            </For>
          </Grid>
        </Show>
        <BillingHoldFlagV2
          flags={orderStore.order.flags ?? []}
          updateFlags={(vals: FlagViewModel[] | Partial<IOrderViewModel>) => {
            if ('length' in vals && Array.isArray(vals)) {
              setOrderStore('order', 'flags', vals);
            } else {
              updateOrderState(vals);
            }
          }}
          orderId={orderStore.order.id}
        />
        <div class={cls.divider} />
      </Show>
      <CustomerInformationTabs />
      <CreditAcknowledgementModal />
      <CreditIncreaseRequestModal />
    </CollapsibleCard>
  );
};
